import {
  ChangeEvent,
  Dispatch,
  FC,
  SetStateAction,
  useEffect,
  useState,
} from 'react'

import { useReactiveVar } from '@apollo/client'
import { RadioButton } from '@joor/design-system'
import ceil from 'lodash/ceil'

import {
  ExternalPaymentQuery,
  PartialPaymentRequest,
} from '__generated__/atlas-types'

import { Flex } from 'styles/components'
import { decimalWithCommas } from 'utils/formatters'

import { Loader } from 'components/Core'

import { BillingAddressDictionary, Order, RateOffer } from '../../types'
import {
  bankTransferSessionAwaitingInstructions,
  iframePaymentSession,
  temporaryPendingPayments,
} from '../store'
import ReachIframe from './ReachIframe'
import {
  useLocalCurrencyData,
  useOrderPartialPaymentRequests,
  useOrderRemainingBalance,
} from './payment.hooks'
import {
  DisabledBody,
  DisabledHeader,
  ErrorMessage,
  MakePaymentCard,
  MediumText,
  SelectPayment,
  StyledRadioButtonGroup,
} from './styles'

// model needed for graphene query that we will be removed in the future once we use the atlas query for payments
export type OldPaymentOrderInfo = Pick<
  Order,
  | 'id'
  | 'billingAddress'
  | 'brand'
  | 'brandName'
  | 'currencyCode'
  | 'lineItems'
  | 'referenceNumber'
  | 'retailer'
  | 'shippingEmail'
  | 'shippingPrice'
  | 'taxAmount'
  | 'discountedTotalRange'
  | 'grandTotal'
  | 'partialPaymentRequests'
  | 'financials'
>

interface PropsType {
  order?: OldPaymentOrderInfo
  externalPayment?: ExternalPaymentQuery['externalPaymentByLink']
  billingAddressDictionary: BillingAddressDictionary
  brandId?: string
  retailerId?: string
  shouldUseLocalCurrencies: boolean
  rateOffer?: RateOffer
  localCurrency?: string
  setForceRerenderDate: Dispatch<SetStateAction<Date>>
  totalAmount: number
  externalPaymentTotal?: number
  disablePayment?: boolean
  partialPaymentRequest?: PartialPaymentRequest
}

enum PaymentOptions {
  ALL = 'all',
  PARTIAL = 'partial',
}

const Payment: FC<PropsType> = ({
  order,
  externalPayment,
  brandId,
  retailerId,
  shouldUseLocalCurrencies,
  rateOffer,
  localCurrency,
  totalAmount,
  disablePayment = false,
  partialPaymentRequest = {} as PartialPaymentRequest,
  ...rest
}) => {
  // Use this function to prevent rounding errors in float representation
  // Rate offers from Reach use four significant decimal points.
  const toFixedNumber = (number: number, precision: number = 4) => {
    const pow = Math.pow(10, precision)
    return Math.round(number * pow) / pow
  }
  const rate = rateOffer?.Rate
  const remainingBalanceWithCurrenciesExchangedIfAny = ceil(
    shouldUseLocalCurrencies && rate
      ? toFixedNumber(totalAmount * rate)
      : totalAmount,
    2,
  )
  const partialPaymentWithCurrenciesExchangedIfAny =
    partialPaymentRequest?.amount
      ? ceil(
          shouldUseLocalCurrencies && rate
            ? toFixedNumber(parseFloat(partialPaymentRequest?.amount) * rate)
            : parseFloat(partialPaymentRequest?.amount),
          2,
        )
      : 0

  const [paymentAmount, setPaymentAmount] = useState(
    partialPaymentWithCurrenciesExchangedIfAny,
  )
  const [paymentOption, setPaymentOption] = useState<PaymentOptions>(
    PaymentOptions.PARTIAL,
  )

  // Reach's drop-in shows a bank transfer's instructions and nothing else does,
  // so while they're on screen the card is frozen: no option switch, no amount
  // change, and the iframe below stays mounted whatever the balance now says
  // (sc-260309). A new payment creates a new session and lifts this.
  const awaitingBankTransferSession = useReactiveVar(
    bankTransferSessionAwaitingInstructions,
  )
  const liveSession = useReactiveVar(iframePaymentSession)
  const isShowingBankTransferInstructions =
    !!liveSession && awaitingBankTransferSession === liveSession

  if (
    paymentOption === PaymentOptions.PARTIAL &&
    partialPaymentWithCurrenciesExchangedIfAny === 0 &&
    !isShowingBankTransferInstructions
  ) {
    // Now that we are refetching the Payment info query, there is a specific case
    // when a manual payment is created and we no longer have a partial payment
    // if the partial payment is checked and there is no value to pay
    // we need to set the option to all and update the amount to be the full payment
    setPaymentOption(PaymentOptions.ALL)
    setPaymentAmount(remainingBalanceWithCurrenciesExchangedIfAny)
  }

  const handleRadioSelect = (e: ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.value
    setPaymentOption(selected as PaymentOptions)
    if (selected === PaymentOptions.ALL) {
      setPaymentAmount(remainingBalanceWithCurrenciesExchangedIfAny)
    } else if (selected === PaymentOptions.PARTIAL) {
      setPaymentAmount(partialPaymentWithCurrenciesExchangedIfAny)
    }
  }

  const currencyCode = order?.currencyCode ?? externalPayment?.currency
  // Sometimes we got negative 0.01 amountWithCurrenciesExchangedIfAny bc of we doing "ceil" for the decimals
  // Thats why doing the amountWithCurrenciesExchangedIfAny > 0 check here
  const hasRemainingBalance = remainingBalanceWithCurrenciesExchangedIfAny > 0

  const currencyLabel = shouldUseLocalCurrencies
    ? `${localCurrency} (${
        partialPaymentRequest?.amount
          ? decimalWithCommas(partialPaymentRequest?.amount)
          : '-------'
      } ${currencyCode})`
    : currencyCode

  useEffect(() => {
    if (isShowingBankTransferInstructions) {
      return
    }
    if (
      paymentOption === PaymentOptions.PARTIAL &&
      partialPaymentWithCurrenciesExchangedIfAny !== paymentAmount
    ) {
      setPaymentAmount(partialPaymentWithCurrenciesExchangedIfAny)
    }
  }, [
    isShowingBankTransferInstructions,
    paymentOption,
    partialPaymentWithCurrenciesExchangedIfAny,
    paymentAmount,
  ])

  useEffect(() => {
    if (isShowingBankTransferInstructions) {
      return
    }
    if (paymentOption === PaymentOptions.ALL) {
      setPaymentAmount(remainingBalanceWithCurrenciesExchangedIfAny)
    }
  }, [
    isShowingBankTransferInstructions,
    paymentOption,
    remainingBalanceWithCurrenciesExchangedIfAny,
  ])

  const partialPaymentText = `${
    partialPaymentRequest.name
  }: ${decimalWithCommas(
    partialPaymentWithCurrenciesExchangedIfAny,
  )} ${currencyLabel}`

  const partialPaymentGreaterThanRemainingAmount =
    parseFloat(
      partialPaymentRequest.amount ? partialPaymentRequest.amount : '0',
    ) > totalAmount

  const temporaryPendingPayment = temporaryPendingPayments()
  const lastTemporaryPendingPayment =
    temporaryPendingPayment[temporaryPendingPayment.length - 1]

  const savedPayment = shouldUseLocalCurrencies
    ? lastTemporaryPendingPayment?.localAmount
    : lastTemporaryPendingPayment?.amount

  const partialPaymentAlreadyPaid =
    savedPayment === partialPaymentWithCurrenciesExchangedIfAny

  const disablePartialPayment =
    partialPaymentGreaterThanRemainingAmount || partialPaymentAlreadyPaid
  return (
    <MakePaymentCard
      title={
        <Flex colGap="5px" alignItems="center">
          <span>Make a Payment</span>
        </Flex>
      }
      {...(shouldUseLocalCurrencies && {
        infoText:
          'Payments will be paid in local currency and received in price type at current rates.',
      })}
    >
      {disablePayment ? (
        <Flex
          height="270px"
          alignItems="center"
          justifyContent="center"
          flexDirection="column"
          gap="8px"
        >
          <DisabledHeader>Payments not available</DisabledHeader>
          <DisabledBody>
            Payments cannot be made on cancelled orders.
          </DisabledBody>
        </Flex>
      ) : (
        <>
          <SelectPayment flexDirection="column" rowGap="12px">
            {hasRemainingBalance ? (
              <>
                <StyledRadioButtonGroup label="Select Payment Amount">
                  {partialPaymentRequest?.active && (
                    <Flex alignItems="center" colGap="10px">
                      <RadioButton
                        id="pay-partial"
                        name="select_payment"
                        label={partialPaymentText}
                        value={PaymentOptions.PARTIAL}
                        checked={paymentOption === PaymentOptions.PARTIAL}
                        onChange={handleRadioSelect}
                        disabled={disablePartialPayment}
                      />
                    </Flex>
                  )}
                  <RadioButton
                    id="pay-all"
                    name="select_payment"
                    label={
                      shouldUseLocalCurrencies
                        ? `Remaining Balance: ${decimalWithCommas(
                            remainingBalanceWithCurrenciesExchangedIfAny,
                          )} ${localCurrency} (${decimalWithCommas(
                            totalAmount,
                          )} ${currencyCode})`
                        : `Remaining Balance: ${decimalWithCommas(
                            remainingBalanceWithCurrenciesExchangedIfAny,
                          )} ${currencyCode}`
                    }
                    value={PaymentOptions.ALL}
                    checked={paymentOption === PaymentOptions.ALL}
                    onChange={handleRadioSelect}
                  />
                </StyledRadioButtonGroup>
              </>
            ) : (
              <MediumText>No Remaining Balance</MediumText>
            )}
          </SelectPayment>
          {(hasRemainingBalance || isShowingBankTransferInstructions) && (
            <ReachIframe
              amountToPay={paymentAmount}
              brandId={brandId ?? externalPayment!.brandId}
              currencyCode={order?.currencyCode ?? externalPayment!.currency}
              orderId={order?.id}
              retailerId={retailerId}
              localCurrency={localCurrency}
              rateOffer={rateOffer}
              shouldUseLocalCurrencies={shouldUseLocalCurrencies}
              retailerName={
                order?.retailer?.displayName ?? externalPayment?.retailerName
              }
              externalPaymentId={externalPayment?.id}
              shippingEmail={order?.shippingEmail}
              brandName={order?.brandName}
              poNumber={order?.referenceNumber}
              orderTotalAmount={
                order
                  ? Number(order?.financials?.dueTotal?.maxPrice)
                  : externalPayment!.amount
              }
              {...rest}
            />
          )}
        </>
      )}
    </MakePaymentCard>
  )
}

/**
 * PaymentContainer exported by default because this is like HOC which will
 * try to fetch RateOffer from reach, display error if any and decide
 * to use local currencies or not for some certain cases
 */

const PaymentContainer: FC<
  Omit<PropsType, 'rateOffer' | 'localCurrency' | 'totalAmount'> & {
    loading?: boolean
    uuid?: string
    onRatesFetched?: (canConvert: boolean) => void
  }
> = (props) => {
  const {
    loading,
    order,
    externalPayment,
    externalPaymentTotal,
    shouldUseLocalCurrencies,
    retailerId,
    uuid,
    onRatesFetched,
  } = props
  const currencyCode = order?.currencyCode ?? externalPayment?.currency
  const {
    baseRateOffer,
    rateOffer,
    localCurrency,
    loading: loadingRateOffer,
    error: rateOfferError,
  } = useLocalCurrencyData({
    shouldUseLocalCurrencies,
    retailerId,
    order,
    externalPayment,
  })

  if (!loadingRateOffer && !rateOfferError) {
    onRatesFetched?.(!(baseRateOffer?.Currency !== currencyCode || !rateOffer))
  }

  // here we have a currency and amount of the partial payment
  // usually order.currency === partialpayment.currency
  const { partialPaymentRequest, loading: partialPaymentRequestLoading } =
    useOrderPartialPaymentRequests(order?.id, uuid)

  const {
    remainingBalance,
    loading: loadingRemainingBalance,
    error: errorRemainingBalance,
  } = useOrderRemainingBalance(order?.id)

  const error = rateOfferError || errorRemainingBalance

  if (error) {
    return <ErrorMessage>{error.message}</ErrorMessage>
  }

  if (
    loading ||
    loadingRateOffer ||
    loadingRemainingBalance ||
    partialPaymentRequestLoading
  ) {
    return <Loader useFullScreen active={loading} />
  }

  const orderTotalAmount = remainingBalance || externalPaymentTotal || 0

  return (
    <Payment
      {...props}
      totalAmount={orderTotalAmount}
      rateOffer={rateOffer}
      localCurrency={localCurrency}
      partialPaymentRequest={
        order?.partialPaymentRequests
          ? order?.partialPaymentRequests
          : partialPaymentRequest
      }
      {...((baseRateOffer?.Currency !== currencyCode || !rateOffer) && {
        shouldUseLocalCurrencies: false,
      })}
    />
  )
}

export default PaymentContainer
